import type { ReactNode } from "react";
import type { ComponentSpec } from "./Processing/types";
import type { WidgetDefinition } from "./widgetDefinition";
import type { DateRangeFormFields } from "./AddWidgetModal/shared/DateRangeForm";
import type { ControllerKind } from "./Processing/types";
// NB: this module must NOT import `widgetRegistry` (a value) — the definitions import
// the NoX sentinels from here, so a value dependency the other way would be a load-time
// cycle. Registry-walking lives in `widgetContractCheck.ts`.

// ─────────────────────────────────────────────────────────────────────────────
// Widget contract
//
// Every auxiliary per-widget concern (duplication, control-panel apply, info
// popover, …) is a field on the WidgetDefinition. A widget that legitimately does
// not need one opts out with an EXPLICIT `NoX` sentinel rather than omitting the
// field — so "I don't need this" is a visible, reviewable decision, and the admin
// checklist / dev-check can tell "handled" from "forgotten".
//
// See Docs/WIDGET_CONTRACT_DESIGN.md.
// ─────────────────────────────────────────────────────────────────────────────

// ── Explicit opt-out sentinels ───────────────────────────────
export const NoInfo = { __contract: "no-info" } as const;
export const NoControlPanel = { __contract: "no-control-panel" } as const;
export const NoDuplication = { __contract: "no-duplication" } as const;
export const NoPdf = { __contract: "no-pdf" } as const;
export const NoData = { __contract: "no-data" } as const;

export type NoInfo = typeof NoInfo;
export type NoControlPanel = typeof NoControlPanel;
export type NoDuplication = typeof NoDuplication;
export type NoPdf = typeof NoPdf;
export type NoData = typeof NoData;

// ── Handler shapes ───────────────────────────────────────────

/** Regenerate the spec's queryIds / cross-refs on duplicate or import (mutates). */
export type DuplicationHandler<TSpec extends ComponentSpec = ComponentSpec> = (spec: TSpec) => void;

/** Fields the Control Panel can bulk-apply to a widget. */
export interface ControlPanelPatch {
  applyDate: boolean;
  applySubjects: boolean;
  date: DateRangeFormFields;
  subjects: {
    selectedSubjects: string[];
    selectedDrills: string[];
    subjectActiveTab: "athletes" | "drills";
    // Human-readable names resolved from the IDs above. Series-label formatters read
    // these (selectedSubjectNames); without them a bulk apply updates the query but
    // leaves the on-chart label stale.
    selectedSubjectNames: string[];
  };
  /**
   * The controllers each filter section may legally follow, by kind. A section that
   * follows one is skipped: the bulk apply must not overwrite a value the widget no
   * longer owns.
   */
  controllers: Record<ControllerKind, Set<string>>;
}

/** Apply a Control Panel date/subject selection to the spec (mutates). */
export type ControlPanelHandler<TSpec extends ComponentSpec = ComponentSpec> = (
  spec: TSpec,
  patch: ControlPanelPatch,
) => void;

/**
 * Info-popover content provider. `Content` is a component (it may use hooks —
 * useWidgetData, useSelector, etc.) rendered inside the shared `WidgetInfoButton`;
 * it returns the popover body, or null when there is nothing to show.
 */
export interface InfoHandler {
  Content: (props: { id: string }) => ReactNode;
}

/** (g) Placeholder — per-widget description for the AI "Build my Dashboard" prompt. Not yet enabled. */
export interface WidgetDescription {
  summary: string;
  controls: string;
}

// ── Deterministic contract hash ──────────────────────────────

/** Stable, synchronous 53-bit string hash (cyrb53). Deterministic across machines. */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * The contract hash is a deterministic function of the widget's name (`type`) and
 * its `schema` (the declared spec-field descriptor). Changing the schema changes the
 * hash, which invalidates the stored `contractHash` and forces the developer back
 * through the checklist. (D-1: `schema` is a hand-authored descriptor for now; the
 * drift-proof end state is a Zod schema the spec type is inferred from.)
 */
export function computeContractHash(def: Pick<WidgetDefinition, "type" | "schema">): string {
  const canonical = JSON.stringify({ type: def.type, schema: [...(def.schema ?? [])].sort() });
  return cyrb53(canonical).toString(36).padStart(11, "0");
}

